import { EnvironmentProviders, inject, InjectionToken, makeEnvironmentProviders, Type } from '@angular/core';
import { Observable, of } from 'rxjs';

import { RoomAdaptApiService } from './api';

// Context-specific report exporter: DNG/Electron uses IPC+hidden window, CUI opens a new browser tab
export interface RoomAdaptReportExporter {
	exportReport(deviceId: string): Promise<boolean>;
}

export const ROOM_ADAPT_REPORT_EXPORTER = new InjectionToken<RoomAdaptReportExporter>('RoomAdaptReportExporter');

export function provideRoomAdaptReportExporter(service: Type<RoomAdaptReportExporter>): EnvironmentProviders {
	return makeEnvironmentProviders([{ provide: ROOM_ADAPT_REPORT_EXPORTER, useExisting: service }]);
}

// Called by the print report component once the DOM is stable and ready to be captured
export type RoomAdaptPrintReadyFn = () => void;

export const ROOM_ADAPT_PRINT_READY = new InjectionToken<RoomAdaptPrintReadyFn>('RoomAdaptPrintReady');

export function provideRoomAdaptPrintReady(fn: RoomAdaptPrintReadyFn): EnvironmentProviders {
	return makeEnvironmentProviders([{ provide: ROOM_ADAPT_PRINT_READY, useValue: fn }]);
}

// Called by the print report component to get device names for speaker-mic pairs.
// Falls back to DCIDs if names can't be queried (CUI).
export type QueryRoomAdaptDeviceNamesFn = (ids: string[]) => Observable<Record<string, string>>;

export const QUERY_ROOM_ADAPT_DEVICE_NAMES = new InjectionToken<QueryRoomAdaptDeviceNamesFn>(
	'QueryRoomAdaptDeviceNames'
);

// DNG: use bulk device name query via sysapi.
export function provideRoomAdaptDeviceNamesQuery(): EnvironmentProviders {
	return makeEnvironmentProviders([
		{
			provide: QUERY_ROOM_ADAPT_DEVICE_NAMES,
			useFactory: (): QueryRoomAdaptDeviceNamesFn => {
				const api = inject(RoomAdaptApiService);
				return (ids: string[]) => api.getDeviceNames$(ids);
			}
		}
	]);
}

// CUI: use a noop query bc query would always fail via dmp.
export function provideRoomAdaptDeviceNamesQueryNoop(): EnvironmentProviders {
	return makeEnvironmentProviders([
		{ provide: QUERY_ROOM_ADAPT_DEVICE_NAMES, useValue: (): Observable<Record<string, string>> => of({}) }
	]);
}
